import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import { Users, UserPlus, Shield, Mail, Key, KeyRound, Trash2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = {
  notary_owner: "Notario Titular",
  compliance_officer: "Oficial de Cumplimiento",
  lawyer: "Abogado",
  assistant: "Asistente",
  data_entry: "Capturista",
  external_client: "Cliente Externo",
};

const roleColors = {
  notary_owner: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  compliance_officer: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  lawyer: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  assistant: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  data_entry: "bg-slate-500/10 text-slate-400 border-slate-500/20",
  external_client: "bg-slate-600/10 text-slate-500 border-slate-600/20",
};

export default function UserManagement() {
  const { organization } = useOrganization();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showRoleChange, setShowRoleChange] = useState(null);
  const [showResetPwd, setShowResetPwd] = useState(null);
  const [showDelete, setShowDelete] = useState(null);

  const plan = organization?.plan || "free";
  const isFree = plan === "free";
  const ownerCount = users.filter(u => u.role === "owner").length;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        axios.get(`${API}/auth/users`, { withCredentials: true }),
        axios.get(`${API}/auth/roles`, { withCredentials: true }),
      ]);
      setUsers(u.data.items || []);
      setRoles(r.data.roles || []);
    } catch {
      toast.error("Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <Layout>
      <div data-testid="users-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Gestion de Usuarios</h1>
            <p className="text-slate-400 mt-1">Administra usuarios y roles de tu organizacion</p>
          </div>
          {!isFree && (
            <Button onClick={() => setShowInvite(true)} data-testid="invite-user-btn">
              <UserPlus size={16} className="mr-2" /> Invitar Usuario
            </Button>
          )}
        </div>

        {/* Users List */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="users-list">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
            <Users size={18} className="text-teal-400" />
            <h2 className="text-lg font-semibold text-white">Usuarios ({users.length})</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-slate-500">No hay usuarios</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {users.map((u) => {
                const isSelf = currentUser?.user_id === u.user_id;
                const isPending = u.must_change_password && !u.account_activated_at;
                const lastLoginLabel = u.last_login_at
                  ? new Date(u.last_login_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })
                  : "Nunca";
                // Hierarchy-aware UI: hide write actions on targets that
                // outrank the current actor. Backend rejects these anyway
                // with a clear 403, but we strip the dead buttons so the
                // Admin doesn't see "Reset password" on the Owner row.
                const ROLE_RANK = { capturista: 0, compliance_officer: 1, admin: 2, owner: 3 };
                const actorRank = ROLE_RANK[currentUser?.role] ?? -1;
                const targetRank = ROLE_RANK[u.role] ?? -1;
                const canManageTarget = !isSelf && actorRank > targetRank;
                return (
                <div key={u.user_id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`user-${u.user_id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-sm font-medium text-teal-400 shrink-0">
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-white truncate">{u.name || "Sin nombre"}</p>
                        {isSelf && <Badge className="bg-teal-500/10 text-teal-300 border-teal-500/20 text-[10px]">Tú</Badge>}
                        {isPending && (
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]" data-testid={`pending-activation-${u.user_id}`}>
                            <Clock size={10} className="mr-1" /> Pendiente activación
                          </Badge>
                        )}
                        {u.account_activated_at && !u.must_change_password && (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                            <CheckCircle2 size={10} className="mr-1" /> Activo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Mail size={10} /> {u.email}
                      </p>
                      <p className="text-[11px] text-slate-600 mt-0.5">Último acceso: {lastLoginLabel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.mfa_enabled && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                        <Key size={10} className="mr-1" /> MFA
                      </Badge>
                    )}
                    <Badge className={roleColors[u.role] || roleColors.assistant} data-testid={`user-role-${u.user_id}`}>
                      {roleLabels[u.role] || u.role}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => setShowRoleChange(u)} data-testid={`change-role-${u.user_id}`} title="Cambiar rol" disabled={!isSelf && !canManageTarget}>
                      <Shield size={14} />
                    </Button>
                    {canManageTarget && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setShowResetPwd(u)} data-testid={`reset-pwd-${u.user_id}`} title="Restablecer contraseña">
                          <KeyRound size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowDelete(u)} data-testid={`delete-user-${u.user_id}`} title="Eliminar usuario" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Roles Reference */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl mt-6" data-testid="roles-reference">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Roles Disponibles</h2>
          </div>
          <div className="divide-y divide-slate-800">
            {roles.map((r) => (
              <div key={r.role_id} className="px-5 py-3" data-testid={`role-${r.role_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={roleColors[r.role_id] || "bg-slate-700 text-slate-300"}>
                    {r.label}
                  </Badge>
                  <span className="text-xs text-slate-500">({r.permissions?.length} permisos)</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {r.permissions?.slice(0, 8).join(", ")}{r.permissions?.length > 8 ? ` +${r.permissions.length - 8} mas` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>

        <InviteDialog open={showInvite} onClose={() => setShowInvite(false)} onInvited={fetchData} roles={roles} />
        {showRoleChange && (
          <RoleChangeDialog user={showRoleChange} onClose={() => setShowRoleChange(null)} onChanged={fetchData} roles={roles} ownerCount={ownerCount} />
        )}
        {showResetPwd && (
          <ResetPasswordDialog user={showResetPwd} onClose={() => setShowResetPwd(null)} />
        )}
        {showDelete && (
          <DeleteUserDialog user={showDelete} onClose={() => setShowDelete(null)} onDeleted={fetchData} ownerCount={ownerCount} />
        )}
      </div>
    </Layout>
  );
}

function InviteDialog({ open, onClose, onInvited, roles }) {
  // Initialize with first available role (excluding notary_owner)
  const availableRoles = roles.filter(r => r.role_id !== "notary_owner");
  const defaultRole = availableRoles.length > 0 ? availableRoles[0].role_id : "capturista";
  
  const [form, setForm] = useState({ email: "", name: "", role: defaultRole });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!form.email || !form.name) { toast.error("Email y nombre requeridos"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/users/invite`, form, { withCredentials: true });
      setResult(res.data);
      toast.success("Usuario invitado exitosamente");
      onInvited();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al invitar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    const availableRoles = roles.filter(r => r.role_id !== "notary_owner");
    const defaultRole = availableRoles.length > 0 ? availableRoles[0].role_id : "capturista";
    setForm({ email: "", name: "", role: defaultRole });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invitar Usuario</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-sm text-emerald-400 font-medium mb-2">Invitación enviada</p>
              <p className="text-xs text-slate-300">Email: <strong>{result.email}</strong></p>
              <p className="text-xs text-slate-300">Rol: {roleLabels[result.role] || result.role}</p>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                Le enviamos un correo con su contraseña temporal y un link al login.
                Deberá cambiar la contraseña en su primer acceso. La contraseña temporal
                expira en <strong className="text-amber-400">72 horas</strong>.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="invite-close-btn">Cerrar</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input className="bg-slate-800 border-slate-700" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input className="bg-slate-800 border-slate-700" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-email" />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {roles.filter(r => r.role_id !== "notary_owner").map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="invite-submit-btn">
                {submitting ? "Invitando..." : "Invitar"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RoleChangeDialog({ user: targetUser, onClose, onChanged, roles, ownerCount }) {
  const [newRole, setNewRole] = useState(targetUser.role);
  const [submitting, setSubmitting] = useState(false);

  const isSoleOwner = targetUser.role === "owner" && ownerCount <= 1;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/auth/users/${targetUser.user_id}/role`, { role: newRole }, { withCredentials: true });
      toast.success(newRole === "owner" ? "Propiedad transferida. El Owner anterior pasó a Admin." : "Rol actualizado");
      onClose();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar rol");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="role-change-dialog">
        <DialogHeader>
          <DialogTitle>Cambiar Rol — {targetUser.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Rol actual: <Badge className={roleColors[targetUser.role]}>{roleLabels[targetUser.role] || targetUser.role}</Badge></p>
          {isSoleOwner && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 text-sm text-amber-400">
              Este usuario es el único Owner de la organización. Para cambiar su rol, primero asigna el rol Owner a otro usuario — su rol cambiará a Admin automáticamente.
            </div>
          )}
          {!isSoleOwner && (
            <div>
              <Label>Nuevo Rol</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="new-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {roles.map((r) => (
                    <SelectItem key={r.role_id} value={r.role_id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newRole === "owner" && newRole !== targetUser.role && (
                <p className="text-xs text-amber-400 mt-2">
                  Al asignar Owner a este usuario, el Owner actual pasará automáticamente a Admin.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {!isSoleOwner && (
            <Button onClick={handleSubmit} disabled={submitting || newRole === targetUser.role} data-testid="role-change-submit">
              {submitting ? "Actualizando..." : "Cambiar Rol"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ResetPasswordDialog({ user: targetUser, onClose }) {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/users/${targetUser.user_id}/password-reset`,
        {},
        { withCredentials: true }
      );
      toast.success("Reset enviado. El usuario recibirá un email con el enlace para definir su nueva contraseña.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al restablecer contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="reset-pwd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} className="text-amber-400" /> Restablecer contraseña
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Le enviaremos a <strong className="text-white">{targetUser.name || targetUser.email}</strong> un enlace para definir su nueva contraseña.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-200 leading-relaxed">
          <p>• El usuario recibirá un correo con un enlace único.</p>
          <p>• El enlace expira en <strong>60 minutos</strong>.</p>
          <p>• Define una nueva contraseña al abrirlo; no usamos credenciales temporales.</p>
          <p>• Sus sesiones activas se cerrarán al confirmar el cambio.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="reset-pwd-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
            {submitting ? "Enviando..." : "Enviar enlace de reset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DeleteUserDialog({ user: targetUser, onClose, onDeleted, ownerCount }) {
  const [confirmText, setConfirmText] = useState("");
  const [secondConfirm, setSecondConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isSoleOwner = targetUser.role === "owner" && ownerCount <= 1;
  const expectedConfirm = targetUser.email;
  const canSubmit = !isSoleOwner && confirmText.trim() === expectedConfirm && secondConfirm;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.delete(
        `${API}/auth/users/${targetUser.user_id}`,
        { withCredentials: true }
      );
      toast.success("Usuario eliminado");
      onClose();
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 size={18} className="text-rose-400" /> Eliminar usuario
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Esta acción desactiva la cuenta de <strong className="text-white">{targetUser.email}</strong>. Sus sesiones activas se cerrarán inmediatamente.
          </DialogDescription>
        </DialogHeader>

        {isSoleOwner && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 text-sm text-amber-300">
            No puedes eliminar al único Owner. Primero transfiere el rol a otro usuario.
          </div>
        )}

        {!isSoleOwner && (
          <>
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4 text-sm text-rose-200" data-testid="stripe-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-rose-300 mb-1">Eliminar este usuario NO reduce tu factura</p>
                  <p className="text-rose-200/90 leading-relaxed">
                    El asiento sigue activo y se sigue cobrando hasta que lo liberes manualmente en <strong>Suscripción</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">
                  Para confirmar, escribe el email completo del usuario: <code className="text-slate-300">{expectedConfirm}</code>
                </Label>
                <Input
                  className="bg-slate-800 border-slate-700 mt-1"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expectedConfirm}
                  data-testid="delete-confirm-input"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={secondConfirm}
                  onChange={(e) => setSecondConfirm(e.target.checked)}
                  data-testid="delete-second-confirm"
                />
                <span>Entiendo que el asiento de Stripe sigue activo después de eliminar este usuario y debo liberarlo manualmente desde Suscripción.</span>
              </label>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {!isSoleOwner && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              data-testid="delete-user-submit-btn"
              className="bg-rose-500 hover:bg-rose-600 text-white disabled:bg-slate-700 disabled:text-slate-500"
            >
              {submitting ? "Eliminando..." : "Eliminar definitivamente"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
